import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { getSalesMenu } from "../../../../../services/Admin/SalesScreenApi";
import { addOrderItems } from "../../../../../services/Admin/OrdersApi";
import AddCustomDrinkTab from "./AddCustomDrinkTab";

const getVisibleProducts = (products) =>
	Array.isArray(products)
		? products.filter((product) => Number(product?.status) !== 0)
		: [];

const AddItemDialog = ({ open, onClose, orderId, onAdded }) => {
	const [currentTab, setCurrentTab] = useState(0);
	
	const [loadingProducts, setLoadingProducts] = useState(false);
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState("");
	
	const [selectedDrink, setSelectedDrink] = useState("");

	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [successMsg, setSuccessMsg] = useState("");

	const [form, setForm] = useState({
		product_id: "",
		drink_id: "",
		quantity: "1",
		price: "",
		note: "",
		status: "pending",
	});

	// Load categories and products when dialog opens
	useEffect(() => {
		let mounted = true;
		const load = async () => {
			setLoadingProducts(true);
			try {
				const res = await getSalesMenu();
				if (!mounted) return;
				// Save categories with their products
				if (Array.isArray(res?.data)) {
					setCategories(res.data);
				}
			} catch (err) {
				console.error("Failed to load menu:", err);
			} finally {
				if (mounted) setLoadingProducts(false);
			}
		};

		if (open) load();
		return () => (mounted = false);
	}, [open]);

	// Reset form when dialog closes
	useEffect(() => {
		if (!open) {
			setCurrentTab(0);
			setForm({
				product_id: "",
				drink_id: "",
				quantity: "1",
				price: "",
				note: "",
				status: "pending",
			});
			setSelectedCategory("");
			setSelectedDrink("");
			setError("");
			setSuccessMsg("");
		}
	}, [open]);

	// Get filtered products based on selected category
	const getFilteredProducts = () => {
		if (!selectedCategory) return [];
		const category = categories.find(cat => cat.id === selectedCategory);
		return getVisibleProducts(category?.products);
	};

	const filteredProducts = getFilteredProducts();

	// Update price when product is selected
	useEffect(() => {
		if (form.product_id) {
			const selectedProduct = filteredProducts.find(p => p.id === form.product_id);
			if (selectedProduct?.price) {
				setForm(prev => ({ ...prev, price: selectedProduct.price }));
			}
		}
	}, [form.product_id, filteredProducts]);

	// Reset product when category changes
	useEffect(() => {
		setForm(prev => ({ ...prev, product_id: "", price: "" }));
	}, [selectedCategory]);

	// Reset fields when switching tabs
	useEffect(() => {
		setForm(prev => ({ 
			...prev, 
			product_id: "", 
			drink_id: "",
			price: "", 
			note: "",
			quantity: "1"
		}));
		setSelectedCategory("");
		setSelectedDrink("");
		setError("");
	}, [currentTab]);

	const handleChange = (key) => (e) => {
		setForm((prev) => ({ ...prev, [key]: e.target.value }));
	};

	const handleFormChange = (key, value) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const handleDrinkSelect = (drinkId, drink) => {
		setSelectedDrink(drinkId);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccessMsg("");

		// Validation
		if (!orderId) {
			setError("رقم الطلب مطلوب");
			return;
		}

		// Validate based on current tab
		if (currentTab === 0) {
			// Products tab
			if (!form.product_id) {
				setError("يجب اختيار المنتج");
				return;
			}
		} else {
			// AI Drinks tab
			if (!selectedDrink) {
				setError("يجب اختيار المشروب");
				return;
			}
		}

		if (!form.quantity || form.quantity <= 0) {
			setError("الكمية يجب أن تكون أكبر من صفر");
			return;
		}
		if (!form.price || form.price <= 0) {
			setError("السعر يجب أن يكون أكبر من صفر");
			return;
		}

		setSubmitting(true);
		try {
			const data = {
				order_id: orderId,
				items: [
					{
						product_id: currentTab === 0 ? parseInt(form.product_id) : null,
						drink_id: currentTab === 1 ? parseInt(selectedDrink) : null,
						quantity: parseInt(form.quantity),
						price: parseFloat(form.price),
						note: form.note || "",
						status: "pending",
					}
				]
			};

			const res = await addOrderItems(data);

			// Success: show message, reset form and notify parent
			setSuccessMsg(currentTab === 0 ? "تمت إضافة الصنف بنجاح" : "تمت إضافة المشروب بنجاح");
			onAdded && onAdded(res?.data || null);
			
			// Reset form
			setForm({
				product_id: "",
				drink_id: "",
				quantity: "1",
				price: "",
				note: "",
				status: "pending",
			});
			setSelectedCategory("");
			setSelectedDrink("");
		} catch (err) {
			setError(err?.message || "فشل إضافة الصنف إلى الطلب");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={Boolean(open)} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>إضافة عنصر إلى الطلب</DialogTitle>

			<Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
				<Tab label="المنتجات" />
				<Tab label="مشروبات العملاء" />
			</Tabs>

			<DialogContent dividers>
				{successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				{/* Tab 0: Products */}
				{currentTab === 0 && (
					<Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, pt: 1 }}>
						{/* Category Selection */}
						<FormControl fullWidth required>
							<InputLabel id="category-label">الفئة</InputLabel>
							<Select 
								labelId="category-label" 
							value={selectedCategory} 
							label="الفئة" 
							onChange={(e) => setSelectedCategory(e.target.value)}
							disabled={loadingProducts}
						>
							<MenuItem value="">-- اختر الفئة --</MenuItem>
							{categories.map((cat) => (
								<MenuItem key={cat.id} value={cat.id}>
									{cat.name}
								</MenuItem>
							))}
						</Select>
						{loadingProducts && (
							<Typography variant="caption" sx={{ mt: 0.5 }}>
								جاري تحميل القائمة...
							</Typography>
						)}
					</FormControl>

					{/* Product Selection */}
					<FormControl fullWidth required disabled={!selectedCategory}>
						<InputLabel id="product-label">المنتج</InputLabel>
						<Select 
							labelId="product-label" 
							value={form.product_id} 
							label="المنتج" 
							onChange={handleChange("product_id")}
						>
							<MenuItem value="">-- اختر المنتج --</MenuItem>
							{filteredProducts.map((p) => (
								<MenuItem key={p.id} value={p.id}>
									{p.name} {p.price && `- ${p.price} ج.م`}
								</MenuItem>
							))}
						</Select>
						{!selectedCategory && (
							<Typography variant="caption" sx={{ mt: 0.5, color: "text.secondary" }}>
								اختر الفئة أولاً
							</Typography>
						)}
					</FormControl>

					<TextField 
						label="الكمية" 
						type="number"
						value={form.quantity} 
						onChange={handleChange("quantity")} 
						fullWidth 
						required
						inputProps={{ min: 1 }}
					/>

					<TextField 
						label="السعر" 
						type="number"
						value={form.price} 
						onChange={handleChange("price")} 
						fullWidth 
						required
						disabled={!!form.product_id}
						inputProps={{ min: 0, step: 0.01 }}
					/>

					<TextField 
						label="ملاحظات" 
						value={form.note} 
						onChange={handleChange("note")} 
						fullWidth 
						multiline 
						rows={2}
						placeholder="مثال: بدون سكر"
					/>
				</Box>
				)}

				{/* Tab 1: AI Drinks */}
				{currentTab === 1 && (
					<AddCustomDrinkTab 
						open={open}
						form={form}
						onFormChange={handleFormChange}
						onDrinkSelect={handleDrinkSelect}
					/>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} disabled={submitting}>إلغاء</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={submitting}>
					{submitting ? <CircularProgress size={20} /> : "إضافة"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddItemDialog;
